import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormacao } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import { estadoNormalizado } from '@/utils/estadoUtils';

type Severidade = 'critico' | 'aviso' | 'info';
type PainelAtivo = 'consolidado' | 'funcionarios' | 'formacoes' | 'orcamentos';

interface AlertaFuncionario {
  id: string;
  nome: string;
  carreira: string;
  unidade: string;
  meses: number;
  ultimaData: string | null;
  ultimaNome: string | null;
  severidade: Severidade;
}

const SEV: Record<Severidade, {
  label: string; icon: string;
  bg: string; text: string; border: string; dot: string; rowBg: string;
}> = {
  critico: {
    label: 'Crítico',  icon: 'ri-alarm-warning-line',
    bg: 'bg-rose-50',   text: 'text-rose-700',   border: 'border-rose-200',   dot: 'bg-rose-500',   rowBg: 'bg-rose-50/30',
  },
  aviso: {
    label: 'Aviso',    icon: 'ri-error-warning-line',
    bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',  dot: 'bg-amber-500',  rowBg: 'bg-amber-50/20',
  },
  info: {
    label: 'Atenção',  icon: 'ri-information-line',
    bg: 'bg-zinc-100',  text: 'text-zinc-600',   border: 'border-zinc-200',   dot: 'bg-zinc-400',   rowBg: '',
  },
};

function mesesLabel(m: number): string {
  if (m === -1) return 'Nunca formado';
  if (m === 0) return 'Menos de 1 mês';
  if (m === 1) return '1 mês';
  return `${m} meses`;
}

const ANO_ATUAL = new Date().getFullYear();

export default function AlertasFormacaoPage() {
  const navigate = useNavigate();
  const { formacoes, participantes } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();

  const [painelAtivo, setPainelAtivo] = useState<PainelAtivo>('consolidado');
  const [limiarCritico, setLimiarCritico] = useState(18);
  const [limiarAviso, setLimiarAviso] = useState(12);
  const [limiarInfo, setLimiarInfo] = useState(6);
  const [search, setSearch] = useState('');
  const [sevFilter, setSevFilter] = useState<'' | Severidade>('');
  const [unidadeFilter, setUnidadeFilter] = useState('');
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [expandido, setExpandido] = useState<string | null>(null);
  const [anoSel, setAnoSel] = useState(ANO_ATUAL);

  const hoje = useMemo(() => new Date(), []);

  // ── 1. Alertas de funcionários sem formação ────────────────────────────────
  const todosAlertas = useMemo<AlertaFuncionario[]>(() => {
    return funcionarios
      .map((f) => {
        const parts = participantes.filter((p) => p.funcionario_id === f.id);
        let ultimaData: string | null = null;
        let ultimaNome: string | null = null;

        parts.forEach((p) => {
          const form = formacoes.find((fc) => fc.id === p.formacao_id);
          const dataRef = form?.data_fim ?? form?.data_inicio ?? null;
          if (dataRef && (!ultimaData || dataRef > ultimaData)) {
            ultimaData = dataRef;
            ultimaNome = form?.nome ?? null;
          }
        });

        const meses = ultimaData
          ? Math.floor((hoje.getTime() - new Date(ultimaData).getTime()) / (1000 * 60 * 60 * 24 * 30.44))
          : -1;

        let sev: Severidade | null = null;
        const m = meses === -1 ? 9999 : meses;
        if (m >= limiarCritico) sev = 'critico';
        else if (m >= limiarAviso) sev = 'aviso';
        else if (m >= limiarInfo) sev = 'info';

        if (!sev) return null;

        return {
          id: f.id,
          nome: f.nome,
          carreira: f.carreira ?? '—',
          unidade: (f as { unidade_organica?: string }).unidade_organica ?? '—',
          meses,
          ultimaData,
          ultimaNome,
          severidade: sev,
        } as AlertaFuncionario;
      })
      .filter((a): a is AlertaFuncionario => a !== null)
      .sort((a, b) => {
        if (a.severidade !== b.severidade) {
          const order = { critico: 0, aviso: 1, info: 2 };
          return order[a.severidade] - order[b.severidade];
        }
        const ma = a.meses === -1 ? 9999 : a.meses;
        const mb = b.meses === -1 ? 9999 : b.meses;
        return mb - ma;
      });
  }, [funcionarios, participantes, formacoes, limiarCritico, limiarAviso, limiarInfo, hoje]);

  // ── 2. Formações sem inscritos ─────────────────────────────────────────────
  const formacoesSemInscritos = useMemo(() => {
    return formacoes
      .filter((f) => {
        if (estadoNormalizado(f.estado) === 'cancelada') return false;
        const count = participantes.filter((p) => p.formacao_id === f.id).length;
        return count === 0;
      })
      .sort((a, b) => {
        // Mais urgentes primeiro (data de início mais próxima)
        const da = a.data_inicio ?? '9999';
        const db = b.data_inicio ?? '9999';
        return da.localeCompare(db);
      });
  }, [formacoes, participantes]);

  // ── 3. Orçamentos a esgotar ────────────────────────────────────────────────
  const orcamentosEsgotar = useMemo(() => {
    return formacoes
      .filter((f) => {
        if (estadoNormalizado(f.estado) === 'cancelada') return false;
        if (f.orcamento_previsto <= 0) return false;
        const pct = (f.custo / f.orcamento_previsto) * 100;
        return pct >= 70; // alerta a partir de 70%
      })
      .map((f) => ({
        ...f,
        pctExecutado: Math.round((f.custo / f.orcamento_previsto) * 100),
        restante: f.orcamento_previsto - f.custo,
      }))
      .sort((a, b) => b.pctExecutado - a.pctExecutado);
  }, [formacoes]);

  // ── 4. LNF por agendar (carreiras sem formação no ano) ─────────────────────
  const lnfPorAgendar = useMemo(() => {
    // Agrupa funcionários por carreira
    const carreirasMap = new Map<string, typeof funcionarios>();
    funcionarios.forEach((f) => {
      const c = f.carreira ?? 'Sem carreira';
      if (!carreirasMap.has(c)) carreirasMap.set(c, []);
      carreirasMap.get(c)!.push(f);
    });

    const resultado: {
      carreira: string;
      totalFuncs: number;
      semFormacao: number;
      pctSemCobertura: number;
      funcionariosSem: typeof funcionarios;
    }[] = [];

    carreirasMap.forEach((funcs, carreira) => {
      const semFormacao = funcs.filter((func) => {
        const partsFunc = participantes.filter((p) => p.funcionario_id === func.id);
        return !partsFunc.some((p) => {
          const form = formacoes.find((f) => f.id === p.formacao_id);
          return form?.data_inicio &&
            new Date(form.data_inicio).getFullYear() === anoSel &&
            estadoNormalizado(form.estado) !== 'cancelada';
        });
      });

      if (semFormacao.length > 0) {
        resultado.push({
          carreira,
          totalFuncs: funcs.length,
          semFormacao: semFormacao.length,
          pctSemCobertura: Math.round((semFormacao.length / funcs.length) * 100),
          funcionariosSem: semFormacao,
        });
      }
    });

    return resultado.sort((a, b) => b.pctSemCobertura - a.pctSemCobertura);
  }, [funcionarios, participantes, formacoes, anoSel]);

  // ── Filtros para funcionários ──────────────────────────────────────────────
  const unidades = useMemo(() => {
    const set = new Set(todosAlertas.map((a) => a.unidade).filter(Boolean));
    return Array.from(set).sort();
  }, [todosAlertas]);

  const visiveis = useMemo(() => todosAlertas.filter((a) => {
    if (dismissed.has(a.id)) return false;
    if (search && !a.nome.toLowerCase().includes(search.toLowerCase()) && !a.carreira.toLowerCase().includes(search.toLowerCase())) return false;
    if (sevFilter && a.severidade !== sevFilter) return false;
    if (unidadeFilter && a.unidade !== unidadeFilter) return false;
    return true;
  }), [todosAlertas, dismissed, search, sevFilter, unidadeFilter]);

  const countBySev = useMemo(() => ({
    critico: visiveis.filter((a) => a.severidade === 'critico').length,
    aviso:   visiveis.filter((a) => a.severidade === 'aviso').length,
    info:    visiveis.filter((a) => a.severidade === 'info').length,
  }), [visiveis]);

  const countTotal = useMemo(() => ({
    critico: todosAlertas.filter((a) => a.severidade === 'critico').length,
    aviso:   todosAlertas.filter((a) => a.severidade === 'aviso').length,
    info:    todosAlertas.filter((a) => a.severidade === 'info').length,
  }), [todosAlertas]);

  const dismiss = useCallback((id: string) => setDismissed((prev) => new Set([...prev, id])), []);
  const dismissAll = useCallback(() => setDismissed(new Set(visiveis.map((a) => a.id))), [visiveis]);

  function formacoesDoFunc(funcId: string) {
    return participantes
      .filter((p) => p.funcionario_id === funcId)
      .map((p) => {
        const f = formacoes.find((fc) => fc.id === p.formacao_id);
        return f ? { nome: f.nome, data: f.data_fim ?? f.data_inicio ?? null, certificado: p.certificado_emitido } : null;
      })
      .filter((x): x is { nome: string; data: string | null; certificado: boolean } => x !== null)
      .sort((a, b) => (b.data ?? '') > (a.data ?? '') ? 1 : -1);
  }

  const semFormacaoCount = funcionarios.length - new Set(participantes.map((p) => p.funcionario_id)).size;

  // Totais para o painel consolidado
  const totalAlertas = todosAlertas.length;
  const totalLnf = lnfPorAgendar.reduce((s, l) => s + l.semFormacao, 0);
  const totalSemInscritos = formacoesSemInscritos.length;
  const totalOrcamentos = orcamentosEsgotar.length;
  const totalCriticos = orcamentosEsgotar.filter((o) => o.pctExecutado >= 90).length;

  const anos = useMemo(() => {
    const set = new Set<number>();
    formacoes.forEach((f) => { if (f.data_inicio) set.add(new Date(f.data_inicio).getFullYear()); });
    set.add(ANO_ATUAL);
    return Array.from(set).sort((a, b) => b - a);
  }, [formacoes]);

  return (
    <main className="flex-1 bg-slate-50/60 overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-zinc-100 px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
              <i className="ri-notification-3-line text-white text-lg" />
            </div>
            <div>
              <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Formação</span>
              <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Alertas Automáticos</h1>
              <p className="text-[12px] text-zinc-400 mt-0.5">
                Painel consolidado de alertas · {semFormacaoCount} funcionário(s) sem qualquer formação registada
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {painelAtivo === 'funcionarios' && visiveis.length > 0 && (
              <button
                onClick={dismissAll}
                className="whitespace-nowrap flex items-center gap-2 text-[13px] font-medium text-zinc-600 border border-zinc-200 bg-white px-4 py-2.5 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-double-line text-sm" /></div>
                Dispensar todos ({visiveis.length})
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-5">

        {/* ── Navegação de painéis ─────────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {([
            {
              id: 'consolidado' as PainelAtivo,
              icon: 'ri-dashboard-3-line',
              label: 'Vista Consolidada',
              desc: 'Todos os alertas numa vista',
              count: totalAlertas + totalLnf + totalSemInscritos + totalOrcamentos,
              countLabel: 'alertas totais',
              bg: 'bg-zinc-900',
              textColor: 'text-white',
              borderActive: 'ring-zinc-900',
            },
            {
              id: 'funcionarios' as PainelAtivo,
              icon: 'ri-user-unfollow-line',
              label: 'Sem Formação',
              desc: `Funcionários há mais de ${limiarInfo} meses`,
              count: totalAlertas,
              countLabel: 'funcionários',
              bg: 'bg-rose-50',
              textColor: 'text-rose-700',
              borderActive: 'ring-rose-400',
            },
            {
              id: 'formacoes' as PainelAtivo,
              icon: 'ri-group-line',
              label: 'Sem Inscritos',
              desc: 'Formações sem participantes',
              count: totalSemInscritos,
              countLabel: 'formações',
              bg: 'bg-amber-50',
              textColor: 'text-amber-700',
              borderActive: 'ring-amber-400',
            },
            {
              id: 'orcamentos' as PainelAtivo,
              icon: 'ri-money-euro-circle-line',
              label: 'Orçamentos',
              desc: 'A esgotar (≥70% executado)',
              count: totalOrcamentos,
              countLabel: `${totalCriticos} críticos`,
              bg: 'bg-orange-50',
              textColor: 'text-orange-700',
              borderActive: 'ring-orange-400',
            },
          ] as const).map((painel) => (
            <button
              key={painel.id}
              onClick={() => setPainelAtivo(painel.id)}
              className={`rounded-xl border p-4 text-left cursor-pointer transition-all ${
                painelAtivo === painel.id
                  ? `ring-2 ${painel.borderActive} border-transparent`
                  : 'border-zinc-100 bg-white hover:border-zinc-200'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${painel.bg}`}>
                  <i className={`${painel.icon} text-base ${painel.textColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-bold text-zinc-900">{painel.label}</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5 leading-tight">{painel.desc}</p>
                </div>
              </div>
              <div className="mt-3 flex items-end justify-between">
                <span className={`text-[24px] font-black ${painel.textColor}`}>{painel.count}</span>
                <span className="text-[10px] text-zinc-400">{painel.countLabel}</span>
              </div>
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* PAINEL CONSOLIDADO                                                */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {painelAtivo === 'consolidado' && (
          <div className="space-y-5">
            {/* LNF por agendar */}
            <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50">
                    <i className="ri-calendar-todo-line text-amber-600 text-sm" />
                  </div>
                  <div>
                    <p className="text-[14px] font-bold text-zinc-900">Necessidades LNF por Agendar</p>
                    <p className="text-[11px] text-zinc-400">Carreiras com funcionários sem formação em {anoSel}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center bg-zinc-100 rounded-lg p-0.5 gap-0.5">
                    {anos.slice(0, 3).map((a) => (
                      <button
                        key={a}
                        onClick={() => setAnoSel(a)}
                        className={`whitespace-nowrap px-3 py-1 rounded-md text-[11px] font-semibold transition-all cursor-pointer ${anoSel === a ? 'bg-white text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => navigate(`/formacao/acoes?ano=${anoSel}&nova=1`)}
                    className="whitespace-nowrap flex items-center gap-1.5 bg-amber-600 text-white text-[12px] font-semibold px-3 py-2 rounded-lg hover:bg-amber-700 transition-colors cursor-pointer"
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-add-line text-xs" /></div>
                    Nova formação
                  </button>
                </div>
              </div>
              {lnfPorAgendar.length === 0 ? (
                <div className="px-6 py-8 flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-100">
                    <i className="ri-checkbox-circle-line text-emerald-600 text-base" />
                  </div>
                  <p className="text-[13px] font-semibold text-emerald-700">Todas as carreiras têm formação associada em {anoSel}</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-50">
                  {lnfPorAgendar.slice(0, 5).map((lnf) => (
                    <div key={lnf.carreira} className="px-6 py-3.5 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-[13px] font-bold text-zinc-800">{lnf.carreira}</p>
                          <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            {lnf.semFormacao} sem formação
                          </span>
                        </div>
                        <p className="text-[11px] text-zinc-400 mt-0.5">{lnf.totalFuncs} funcionários no total</p>
                      </div>
                      <div className="w-32 flex-shrink-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] text-zinc-400">Sem cobertura</span>
                          <span className="text-[11px] font-bold text-amber-700">{lnf.pctSemCobertura}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-amber-500"
                            style={{ width: `${lnf.pctSemCobertura}%` }}
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/formacao/acoes?ano=${anoSel}&nova=1`)}
                        className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors cursor-pointer flex-shrink-0"
                        title="Criar formação"
                      >
                        <i className="ri-add-line text-sm" />
                      </button>
                    </div>
                  ))}
                  {lnfPorAgendar.length > 5 && (
                    <div className="px-6 py-3 text-center">
                      <button
                        onClick={() => setPainelAtivo('funcionarios')}
                        className="whitespace-nowrap text-[12px] font-semibold text-zinc-500 hover:text-zinc-700 cursor-pointer"
                      >
                        Ver mais {lnfPorAgendar.length - 5} carreiras →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Formações sem inscritos */}
            <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-50">
                    <i className="ri-group-line text-rose-600 text-sm" />
                  </div>
                  <div>
                    <p className="text-[14px] font-bold text-zinc-900">Formações sem Inscritos</p>
                    <p className="text-[11px] text-zinc-400">Ações planeadas ou em curso sem participantes registados</p>
                  </div>
                </div>
                <span className={`text-[12px] font-bold px-3 py-1 rounded-full ${formacoesSemInscritos.length > 0 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {formacoesSemInscritos.length} formação(ões)
                </span>
              </div>
              {formacoesSemInscritos.length === 0 ? (
                <div className="px-6 py-8 flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-100">
                    <i className="ri-checkbox-circle-line text-emerald-600 text-base" />
                  </div>
                  <p className="text-[13px] font-semibold text-emerald-700">Todas as formações têm participantes inscritos</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-50">
                  {formacoesSemInscritos.slice(0, 5).map((f) => (
                    <div key={f.id} className="px-6 py-3.5 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-bold text-zinc-800 truncate">{f.nome}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {f.categoria && (
                            <span className="text-[10px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full">{f.categoria}</span>
                          )}
                          {f.data_inicio && (
                            <span className="text-[10px] text-zinc-400">
                              {new Date(f.data_inicio).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          )}
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                            estadoNormalizado(f.estado) === 'em_curso' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-600'
                          }`}>{f.estado}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-[13px] font-bold text-zinc-700">{f.duracao_horas}h</p>
                        <p className="text-[10px] text-zinc-400">{f.modalidade}</p>
                      </div>
                      <button
                        onClick={() => navigate('/formacao/participantes')}
                        className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors cursor-pointer flex-shrink-0"
                        title="Registar participantes"
                      >
                        <i className="ri-user-add-line text-sm" />
                      </button>
                    </div>
                  ))}
                  {formacoesSemInscritos.length > 5 && (
                    <div className="px-6 py-3 text-center">
                      <button
                        onClick={() => setPainelAtivo('formacoes')}
                        className="whitespace-nowrap text-[12px] font-semibold text-zinc-500 hover:text-zinc-700 cursor-pointer"
                      >
                        Ver mais {formacoesSemInscritos.length - 5} formações →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Orçamentos a esgotar */}
            <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-50">
                    <i className="ri-money-euro-circle-line text-orange-600 text-sm" />
                  </div>
                  <div>
                    <p className="text-[14px] font-bold text-zinc-900">Orçamentos a Esgotar</p>
                    <p className="text-[11px] text-zinc-400">Formações com ≥70% do orçamento executado</p>
                  </div>
                </div>
                <span className={`text-[12px] font-bold px-3 py-1 rounded-full ${orcamentosEsgotar.length > 0 ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {orcamentosEsgotar.length} formação(ões)
                </span>
              </div>
              {orcamentosEsgotar.length === 0 ? (
                <div className="px-6 py-8 flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-100">
                    <i className="ri-checkbox-circle-line text-emerald-600 text-base" />
                  </div>
                  <p className="text-[13px] font-semibold text-emerald-700">Todos os orçamentos estão dentro dos limites</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-50">
                  {orcamentosEsgotar.slice(0, 5).map((f) => {
                    const isCritico = f.pctExecutado >= 90;
                    return (
                      <div key={f.id} className="px-6 py-3.5 flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-[13px] font-bold text-zinc-800 truncate">{f.nome}</p>
                            {isCritico && (
                              <span className="text-[10px] font-bold bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full flex-shrink-0">Crítico</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1.5">
                            <div className="flex-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${f.pctExecutado}%`,
                                  backgroundColor: isCritico ? '#ef4444' : f.pctExecutado >= 80 ? '#f97316' : '#f59e0b',
                                }}
                              />
                            </div>
                            <span className={`text-[11px] font-bold flex-shrink-0 ${isCritico ? 'text-rose-600' : 'text-orange-600'}`}>
                              {f.pctExecutado}%
                            </span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-[12px] font-bold text-zinc-700">
                            {f.custo.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                            <span className="text-zinc-400 font-normal"> / </span>
                            {f.orcamento_previsto.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                          </p>
                          <p className="text-[10px] text-zinc-400">
                            Restam {f.restante.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  {orcamentosEsgotar.length > 5 && (
                    <div className="px-6 py-3 text-center">
                      <button
                        onClick={() => setPainelAtivo('orcamentos')}
                        className="whitespace-nowrap text-[12px] font-semibold text-zinc-500 hover:text-zinc-700 cursor-pointer"
                      >
                        Ver mais {orcamentosEsgotar.length - 5} formações →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* PAINEL FUNCIONÁRIOS SEM FORMAÇÃO                                  */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {painelAtivo === 'funcionarios' && (
          <div className="space-y-5">
            {/* Configuração de limiares */}
            <div className="bg-white rounded-xl border border-zinc-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-settings-3-line text-zinc-400 text-sm" />
                </div>
                <p className="text-[13px] font-bold text-zinc-800">Configurar Limiares de Alerta</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {([
                  { sev: 'critico' as Severidade, label: 'Crítico',  value: limiarCritico, setter: setLimiarCritico, desc: 'Urgente — deve ser tratado imediatamente' },
                  { sev: 'aviso'   as Severidade, label: 'Aviso',    value: limiarAviso,   setter: setLimiarAviso,   desc: 'Requer atenção a curto prazo' },
                  { sev: 'info'    as Severidade, label: 'Atenção',  value: limiarInfo,    setter: setLimiarInfo,    desc: 'Informativo — a monitorizar' },
                ]).map(({ sev, label, value, setter, desc }) => {
                  const c = SEV[sev];
                  return (
                    <div key={sev} className={`rounded-xl border ${c.border} p-4`}>
                      <div className="flex items-center gap-2.5 mb-3">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${c.bg}`}>
                          <i className={`${c.icon} text-sm ${c.text}`} />
                        </div>
                        <div>
                          <p className={`text-[12px] font-bold ${c.text}`}>{label}</p>
                          <p className="text-[10px] text-zinc-400 leading-tight">{desc}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] text-zinc-500">Mais de</span>
                        <input
                          type="number"
                          min={1}
                          max={120}
                          value={value}
                          onChange={(ev) => setter(Number(ev.target.value))}
                          className="w-16 px-2 py-1.5 text-[13px] font-bold border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 text-center"
                        />
                        <span className="text-[12px] text-zinc-500">meses</span>
                      </div>
                      <p className="text-[11px] font-semibold text-zinc-600 mt-2">
                        {countTotal[sev]} funcionário(s)
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Contadores */}
            <div className="grid grid-cols-3 gap-4">
              {(['critico', 'aviso', 'info'] as Severidade[]).map((sev) => {
                const c = SEV[sev];
                const count = countBySev[sev];
                const total = countTotal[sev];
                return (
                  <button
                    key={sev}
                    onClick={() => setSevFilter(sevFilter === sev ? '' : sev)}
                    className={`rounded-xl border ${c.border} p-4 flex items-center gap-3 cursor-pointer transition-all ${
                      sevFilter === sev ? 'ring-2 ring-offset-1 ring-current' : 'hover:opacity-80'
                    } ${c.bg}`}
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white flex-shrink-0">
                      <i className={`${c.icon} text-lg ${c.text}`} />
                    </div>
                    <div className="text-left">
                      <p className={`text-[22px] font-black ${c.text} leading-tight`}>{count}</p>
                      <p className={`text-[11px] font-semibold ${c.text}`}>{c.label}</p>
                      {total !== count && <p className="text-[10px] text-zinc-400">{total} no total</p>}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Filtros */}
            <div className="bg-white rounded-xl border border-zinc-100 p-4 flex flex-wrap gap-2.5 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
                  <i className="ri-search-line text-sm" />
                </div>
                <input
                  type="text"
                  placeholder="Pesquisar funcionário ou carreira..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
                />
              </div>
              {unidades.length > 0 && (
                <select
                  value={unidadeFilter}
                  onChange={(e) => setUnidadeFilter(e.target.value)}
                  className="text-[13px] border border-zinc-200 rounded-lg px-3 py-2.5 bg-white text-zinc-600 focus:outline-none cursor-pointer"
                >
                  <option value="">Todas as unidades</option>
                  {unidades.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              )}
              {dismissed.size > 0 && (
                <button
                  onClick={() => setDismissed(new Set())}
                  className="whitespace-nowrap text-[12px] font-medium text-zinc-400 hover:text-zinc-600 border border-zinc-200 px-3 py-2 rounded-lg cursor-pointer transition-colors"
                >
                  Repor {dismissed.size} dispensado(s)
                </button>
              )}
              <span className="text-[12px] text-zinc-400 ml-auto">
                <span className="font-semibold text-zinc-600">{visiveis.length}</span> alerta(s)
              </span>
            </div>

            {/* Lista */}
            {visiveis.length === 0 ? (
              <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center">
                  <i className="ri-shield-check-line text-5xl text-emerald-300" />
                </div>
                <p className="text-[15px] font-bold text-zinc-400">
                  {todosAlertas.length === 0 ? 'Nenhum alerta para os limiares configurados' : 'Sem alertas para mostrar'}
                </p>
                <p className="text-[12px] text-zinc-300 text-center max-w-[300px]">
                  {dismissed.size > 0 ? `${dismissed.size} alerta(s) dispensados.` : 'Todos os funcionários têm formação dentro dos prazos.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {visiveis.map((alerta) => {
                  const c = SEV[alerta.severidade];
                  const isExp = expandido === alerta.id;
                  const formacoesHist = formacoesDoFunc(alerta.id);

                  return (
                    <div key={alerta.id} className={`rounded-xl border ${c.border} overflow-hidden`}>
                      <div className={`px-5 py-3.5 flex items-center gap-4 ${c.rowBg}`}>
                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${c.bg}`}>
                          <i className={`${c.icon} text-sm ${c.text}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-[13px] font-bold text-zinc-900">{alerta.nome}</p>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${c.bg} ${c.text}`}>
                              {c.label}
                            </span>
                          </div>
                          <p className="text-[11px] text-zinc-400 mt-0.5">
                            {alerta.carreira} · {alerta.unidade}
                          </p>
                        </div>
                        <div className="text-center flex-shrink-0 min-w-[110px]">
                          <p className={`text-[15px] font-black ${c.text}`}>{mesesLabel(alerta.meses)}</p>
                          <p className="text-[10px] text-zinc-400">sem formação</p>
                        </div>
                        <div className="text-right flex-shrink-0 min-w-[140px] hidden lg:block">
                          {alerta.ultimaData ? (
                            <>
                              <p className="text-[12px] font-medium text-zinc-600 max-w-[140px] truncate" title={alerta.ultimaNome ?? ''}>
                                {alerta.ultimaNome ?? '—'}
                              </p>
                              <p className="text-[10px] text-zinc-400">
                                {new Date(alerta.ultimaData).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </p>
                            </>
                          ) : (
                            <span className="text-[12px] font-bold text-zinc-300">Sem registo</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => setExpandido(isExp ? null : alerta.id)}
                            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-white/60 transition-colors cursor-pointer"
                            title="Ver histórico"
                          >
                            <i className={`ri-history-line text-sm ${isExp ? 'text-zinc-700' : ''}`} />
                          </button>
                          <button
                            onClick={() => dismiss(alerta.id)}
                            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-white/60 transition-colors cursor-pointer"
                            title="Dispensar alerta"
                          >
                            <i className="ri-close-line text-sm" />
                          </button>
                        </div>
                      </div>
                      {isExp && (
                        <div className="border-t border-zinc-100 bg-white px-5 py-4">
                          <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wide mb-3">
                            Histórico de formações — {alerta.nome}
                          </p>
                          {formacoesHist.length === 0 ? (
                            <p className="text-[12px] text-zinc-300 italic">Nenhuma formação registada.</p>
                          ) : (
                            <div className="space-y-1.5">
                              {formacoesHist.map((fh, i) => (
                                <div key={i} className="flex items-center gap-3 bg-zinc-50 rounded-lg px-3 py-2">
                                  <div className={`w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 ${fh.certificado ? 'bg-emerald-100' : 'bg-zinc-200'}`}>
                                    <i className={`${fh.certificado ? 'ri-medal-line text-emerald-600' : 'ri-time-line text-zinc-400'} text-[9px]`} />
                                  </div>
                                  <p className="text-[12px] font-medium text-zinc-700 flex-1 min-w-0 truncate">{fh.nome}</p>
                                  <span className="text-[11px] text-zinc-400 flex-shrink-0">
                                    {fh.data ? new Date(fh.data).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                  </span>
                                  <span className={`text-[10px] font-semibold flex-shrink-0 ${fh.certificado ? 'text-emerald-600' : 'text-zinc-400'}`}>
                                    {fh.certificado ? 'Certificado' : 'Sem cert.'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* PAINEL FORMAÇÕES SEM INSCRITOS                                    */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {painelAtivo === 'formacoes' && (
          <div className="space-y-3">
            {formacoesSemInscritos.length === 0 ? (
              <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center">
                  <i className="ri-shield-check-line text-5xl text-emerald-300" />
                </div>
                <p className="text-[15px] font-bold text-zinc-400">Todas as formações têm participantes inscritos</p>
              </div>
            ) : (
              formacoesSemInscritos.map((f) => (
                <div key={f.id} className="bg-white rounded-xl border border-rose-200 px-6 py-4 flex items-center gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-rose-50 flex-shrink-0">
                    <i className="ri-group-line text-rose-600 text-base" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-bold text-zinc-900 truncate">{f.nome}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {f.categoria && <span className="text-[10px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full">{f.categoria}</span>}
                      {f.entidade_formadora && <span className="text-[10px] text-zinc-400">{f.entidade_formadora}</span>}
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                        estadoNormalizado(f.estado) === 'em_curso' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-600'
                      }`}>{f.estado}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 flex-shrink-0">
                    <div className="text-center">
                      <p className="text-[16px] font-black text-zinc-800">{f.duracao_horas}h</p>
                      <p className="text-[10px] text-zinc-400">duração</p>
                    </div>
                    {f.data_inicio && (
                      <div className="text-center">
                        <p className="text-[13px] font-bold text-zinc-700">
                          {new Date(f.data_inicio).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })}
                        </p>
                        <p className="text-[10px] text-zinc-400">início</p>
                      </div>
                    )}
                    <button
                      onClick={() => navigate('/formacao/participantes')}
                      className="whitespace-nowrap flex items-center gap-2 bg-rose-600 text-white text-[12px] font-semibold px-3 py-2 rounded-lg hover:bg-rose-700 transition-colors cursor-pointer"
                    >
                      <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-user-add-line text-xs" /></div>
                      Inscrever
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* PAINEL ORÇAMENTOS                                                 */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {painelAtivo === 'orcamentos' && (
          <div className="space-y-3">
            {orcamentosEsgotar.length === 0 ? (
              <div className="bg-white rounded-xl border border-zinc-100 py-20 flex flex-col items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center">
                  <i className="ri-shield-check-line text-5xl text-emerald-300" />
                </div>
                <p className="text-[15px] font-bold text-zinc-400">Todos os orçamentos estão dentro dos limites</p>
              </div>
            ) : (
              orcamentosEsgotar.map((f) => {
                const isCritico = f.pctExecutado >= 90;
                const barColor = isCritico ? '#ef4444' : f.pctExecutado >= 80 ? '#f97316' : '#f59e0b';
                return (
                  <div key={f.id} className={`bg-white rounded-xl border overflow-hidden ${isCritico ? 'border-rose-200' : 'border-orange-200'}`}>
                    <div className="px-6 py-4 flex items-center gap-4">
                      <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${isCritico ? 'bg-rose-50' : 'bg-orange-50'}`}>
                        <i className={`ri-money-euro-circle-line text-base ${isCritico ? 'text-rose-600' : 'text-orange-600'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-[14px] font-bold text-zinc-900 truncate">{f.nome}</p>
                          {isCritico && (
                            <span className="text-[10px] font-bold bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full flex-shrink-0">Crítico</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex-1 h-2 rounded-full bg-zinc-100 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{ width: `${f.pctExecutado}%`, backgroundColor: barColor }}
                            />
                          </div>
                          <span className={`text-[12px] font-black flex-shrink-0 ${isCritico ? 'text-rose-600' : 'text-orange-600'}`}>
                            {f.pctExecutado}%
                          </span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 min-w-[160px]">
                        <p className="text-[13px] font-bold text-zinc-800">
                          {f.custo.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                          <span className="text-zinc-400 font-normal text-[11px]"> executado</span>
                        </p>
                        <p className="text-[11px] text-zinc-500 mt-0.5">
                          Orçamento: {f.orcamento_previsto.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                        </p>
                        <p className={`text-[11px] font-semibold mt-0.5 ${isCritico ? 'text-rose-600' : 'text-orange-600'}`}>
                          Restam {f.restante.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                        </p>
                      </div>
                    </div>
                    {f.estado && (
                      <div className={`px-6 py-2 border-t ${isCritico ? 'border-rose-100 bg-rose-50/30' : 'border-orange-100 bg-orange-50/30'} flex items-center gap-3`}>
                        <span className="text-[11px] text-zinc-500">Estado:</span>
                        <span className="text-[11px] font-semibold text-zinc-700">{f.estado}</span>
                        {f.data_inicio && (
                          <>
                            <span className="text-zinc-300">·</span>
                            <span className="text-[11px] text-zinc-500">
                              {new Date(f.data_inicio).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}
                              {f.data_fim && ` → ${new Date(f.data_fim).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}`}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </main>
  );
}